import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { queryKeys } from '../api/queryKeys'
import type {
  PhysicalAddressJson,
  PhysicalMailDecryptResponse,
  PhysicalMailDecryptResultData,
  PhysicalMailMutationResponse,
  PhysicalMailMutationResultData,
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { CodeChip } from '../components/CodeChip'
import { ConfirmActionDialog } from '../components/ConfirmActionDialog'
import { useAdminMutation } from '../hooks/useAdminMutation'

// plan-admin/08 — Per-message dispatch detail page.
//
// Two-stage UX:
//   1. Default state: a "Begin dispatch" gate that warns the
//      operator the click will reveal the recipient's address and
//      record an audit event. Begin dispatch is the ONLY way to
//      see the address; reasons are required.
//   2. Address-revealed state: a printable address block plus the
//      three lifecycle actions (Mark dispatched / Mark received /
//      Mark failed), each behind its own ConfirmActionDialog.
//
// The decrypted address is held in component state ONLY: navigating
// away or refreshing returns the page to the gate. We deliberately
// do NOT store the plaintext in TanStack Query so a stale cache
// cannot leak it across navigations.

// review_comments H1 (HEAD 0f55588): the backend now enforces an
// explicit dispatch lifecycle and returns DISPATCH_STATUS_CONFLICT
// (HTTP 409) when the operator tries to perform an action that the
// row is no longer in a valid state for. We surface a short, plain-
// language explanation per action; the operator should refresh the
// queue and re-examine the row before retrying.
const DISPATCH_STATUS_CONFLICT_COPY = {
  decrypt:
    'This row is not in a state that allows decrypting the address. '
    + 'Refresh the queue; the row may have been received or never '
    + 'queued for dispatch.',
  markDispatched:
    'This row cannot be marked dispatched in its current state. '
    + 'Refresh the queue; only PENDING or FAILED rows can be posted.',
  markReceived:
    'This row cannot be marked received in its current state. '
    + 'Only DISPATCHED rows can be marked received — this guard '
    + 'prevents permanently deleting an address whose letter was '
    + 'never posted. Refresh the queue and try again.',
  markFailed:
    'This row cannot be marked failed in its current state. '
    + 'Only PENDING or DISPATCHED rows can be marked failed.',
} as const

const DECRYPT_ERROR_COPY: Record<string, string> = {
  DECRYPT_REASON_REQUIRED: 'Reason is required (4-500 characters).',
  MESSAGE_NOT_FOUND:
    'Message no longer exists. Refresh the queue and try again.',
  ADDRESS_PURGED:
    'This address was permanently deleted when the operator marked '
    + 'the letter received. There is nothing left to decrypt.',
  DISPATCH_STATUS_CONFLICT: DISPATCH_STATUS_CONFLICT_COPY.decrypt,
  PHYSICAL_ADDRESS_KEY_UNAVAILABLE:
    'Server cannot load the physical-address keyring. Contact the '
    + 'platform owner to verify the PHYSICAL_ADDRESS_KEY environment '
    + 'variable is set and well-formed.',
  WRONG_KEY:
    'Server-side key mismatch. Contact the platform owner; the '
    + 'address cannot be decrypted with the active keyring.',
  CIPHER_TAMPER:
    'Server-side data integrity error. Contact the platform owner; '
    + 'the encrypted bytes failed authentication.',
}

const MARK_DISPATCHED_ERROR_COPY: Record<string, string> = {
  MARK_DISPATCHED_REASON_REQUIRED: 'Reason is required (4-500 characters).',
  MESSAGE_NOT_FOUND: 'Message no longer exists.',
  DISPATCH_STATUS_CONFLICT: DISPATCH_STATUS_CONFLICT_COPY.markDispatched,
}

const MARK_RECEIVED_ERROR_COPY: Record<string, string> = {
  MARK_RECEIVED_REASON_REQUIRED: 'Reason is required (4-500 characters).',
  MESSAGE_NOT_FOUND: 'Message no longer exists.',
  DISPATCH_STATUS_CONFLICT: DISPATCH_STATUS_CONFLICT_COPY.markReceived,
}

const MARK_FAILED_ERROR_COPY: Record<string, string> = {
  MARK_FAILED_REASON_REQUIRED: 'Reason is required (4-500 characters).',
  FAILURE_REASON_REQUIRED: 'A failure reason is required.',
  MESSAGE_NOT_FOUND: 'Message no longer exists.',
  DISPATCH_STATUS_CONFLICT: DISPATCH_STATUS_CONFLICT_COPY.markFailed,
}

export function PhysicalMailDispatchPage() {
  const params = useParams<{ messageId: string }>()
  const messageId = params.messageId ?? ''
  const auth = useAuth()
  const canAdmin = auth.requireRole('ADMIN')

  // Decrypted address held in component state only (never in
  // TanStack Query). On unmount this drops; refreshing the page
  // routes back to the Begin-dispatch gate.
  const [revealed, setRevealed] =
    useState<PhysicalMailDecryptResultData | null>(null)
  const [showDecryptDialog, setShowDecryptDialog] = useState(false)
  const [showDispatchedDialog, setShowDispatchedDialog] = useState(false)
  const [showReceivedDialog, setShowReceivedDialog] = useState(false)
  const [showFailedDialog, setShowFailedDialog] = useState(false)
  const [trackingNumberDraft, setTrackingNumberDraft] = useState('')
  const [failureReasonDraft, setFailureReasonDraft] = useState('')
  const [lastMutation, setLastMutation] =
    useState<PhysicalMailMutationResultData | null>(null)

  // Defensive cleanup: explicitly drop the plaintext on unmount.
  // (React would garbage-collect the state anyway, but this keeps
  // the intent visible in code review.)
  useEffect(() => () => { setRevealed(null) }, [])

  const decryptMutation = useAdminMutation<
    { reason: string }, PhysicalMailDecryptResultData
  >({
    mutate: (client, { reason }) =>
      client.post<PhysicalMailDecryptResponse>(
        `/r/admin/physical-mail/${messageId}/decrypt-for-dispatch`,
        { reason },
      ),
    invalidates: [queryKeys.physicalMail()],
  })

  const dispatchedMutation = useAdminMutation<
    { reason: string; tracking_number: string },
    PhysicalMailMutationResultData
  >({
    mutate: (client, vars) =>
      client.post<PhysicalMailMutationResponse>(
        `/r/admin/physical-mail/${messageId}/mark-dispatched`,
        vars,
      ),
    invalidates: [queryKeys.physicalMail(),
                  queryKeys.physicalMail({ includeDispatched: true })],
  })

  const receivedMutation = useAdminMutation<
    { reason: string }, PhysicalMailMutationResultData
  >({
    mutate: (client, vars) =>
      client.post<PhysicalMailMutationResponse>(
        `/r/admin/physical-mail/${messageId}/mark-received`,
        vars,
      ),
    invalidates: [queryKeys.physicalMail(),
                  queryKeys.physicalMail({ includeDispatched: true })],
  })

  const failedMutation = useAdminMutation<
    { reason: string; failure_reason: string },
    PhysicalMailMutationResultData
  >({
    mutate: (client, vars) =>
      client.post<PhysicalMailMutationResponse>(
        `/r/admin/physical-mail/${messageId}/mark-failed`,
        vars,
      ),
    invalidates: [queryKeys.physicalMail(),
                  queryKeys.physicalMail({ includeDispatched: true })],
  })

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">
            Dispatch letter
          </h1>
          <p className="mt-1 text-sm text-stone-500">
            Message <CodeChip>{messageId}</CodeChip>
          </p>
        </div>
        <Link
          to="/physical-mail"
          className="text-sm text-accent hover:underline"
        >
          ← Back to queue
        </Link>
      </header>

      {!canAdmin && (
        <div
          className="rounded-md border border-stone-200 bg-stone-50 p-4
                     text-sm text-stone-700"
          data-testid="admin-required-notice"
        >
          Admin role required to dispatch physical mail. Sign in as
          an admin to begin dispatch.
        </div>
      )}

      {canAdmin && !revealed && (
        <BeginDispatchGate
          onClick={() => setShowDecryptDialog(true)}
        />
      )}

      {canAdmin && revealed && (
        <RevealedAddress
          revealed={revealed}
          onPrint={() => window.print()}
          onMarkDispatched={() => setShowDispatchedDialog(true)}
          onMarkReceived={() => setShowReceivedDialog(true)}
          onMarkFailed={() => setShowFailedDialog(true)}
        />
      )}

      {lastMutation && <MutationSummary result={lastMutation} />}

      {showDecryptDialog && (
        <ConfirmActionDialog
          title="Begin dispatch"
          body={
            <span>
              This will reveal the recipient's physical address and
              is recorded in the audit log as{' '}
              <CodeChip>DECRYPT_PHYSICAL_ADDRESS</CodeChip>. Provide
              a reason that links the decrypt to the postal-batch /
              ticket you are working from.
            </span>
          }
          confirmLabel="Begin dispatch"
          errorMessages={DECRYPT_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            const result = await decryptMutation.mutateAsync({ reason })
            setRevealed(result)
            setShowDecryptDialog(false)
          }}
          onCancel={() => setShowDecryptDialog(false)}
        />
      )}

      {showDispatchedDialog && (
        <ConfirmActionDialog
          title="Mark as dispatched"
          body={
            <div className="space-y-2">
              <p>
                Records that the letter has been posted. Audited as{' '}
                <CodeChip>MARK_PHYSICAL_DISPATCHED</CodeChip>.
              </p>
              <label className="block text-xs text-stone-700">
                Tracking number (optional):
                <input
                  type="text"
                  value={trackingNumberDraft}
                  onChange={(e) => setTrackingNumberDraft(e.target.value)}
                  className="mt-1 block w-full rounded border border-stone-300 px-2 py-1 text-xs"
                  data-testid="tracking-number-input"
                />
              </label>
            </div>
          }
          confirmLabel="Mark dispatched"
          errorMessages={MARK_DISPATCHED_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            const result = await dispatchedMutation.mutateAsync({
              reason,
              tracking_number: trackingNumberDraft.trim(),
            })
            setLastMutation(result)
            setShowDispatchedDialog(false)
            setTrackingNumberDraft('')
          }}
          onCancel={() => {
            setShowDispatchedDialog(false)
            setTrackingNumberDraft('')
          }}
        />
      )}

      {showReceivedDialog && (
        <ConfirmActionDialog
          title="Mark as received"
          body={
            <span>
              The recipient confirmed receipt. This{' '}
              <strong>permanently deletes</strong> the encrypted
              address from storage and cannot be undone. Audited as{' '}
              <CodeChip>MARK_PHYSICAL_RECEIVED</CodeChip>.
            </span>
          }
          confirmLabel="Mark received"
          confirmTwice
          errorMessages={MARK_RECEIVED_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            const result = await receivedMutation.mutateAsync({ reason })
            setLastMutation(result)
            // After receipt the address is gone; drop the in-memory
            // copy so the page returns to a "purged" view.
            setRevealed(null)
            setShowReceivedDialog(false)
          }}
          onCancel={() => setShowReceivedDialog(false)}
        />
      )}

      {showFailedDialog && (
        <ConfirmActionDialog
          title="Mark as failed"
          body={
            <div className="space-y-2">
              <p>
                Records that the letter could not be delivered. The
                ciphertext is retained so a retry remains possible.
                Audited as{' '}
                <CodeChip>MARK_PHYSICAL_FAILED</CodeChip>.
              </p>
              <label className="block text-xs text-stone-700">
                Failure reason (required):
                <input
                  type="text"
                  value={failureReasonDraft}
                  onChange={(e) => setFailureReasonDraft(e.target.value)}
                  className="mt-1 block w-full rounded border border-stone-300 px-2 py-1 text-xs"
                  data-testid="failure-reason-input"
                />
              </label>
            </div>
          }
          confirmLabel="Mark failed"
          errorMessages={MARK_FAILED_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            const result = await failedMutation.mutateAsync({
              reason,
              failure_reason: failureReasonDraft.trim(),
            })
            setLastMutation(result)
            setShowFailedDialog(false)
            setFailureReasonDraft('')
          }}
          onCancel={() => {
            setShowFailedDialog(false)
            setFailureReasonDraft('')
          }}
        />
      )}
    </div>
  )
}

function BeginDispatchGate({ onClick }: { onClick: () => void }) {
  return (
    <section
      className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm"
      data-testid="begin-dispatch-gate"
    >
      <h2 className="text-base font-medium text-stone-900">
        Address is encrypted at rest
      </h2>
      <p className="mt-2 text-sm text-stone-700">
        Click <strong>Begin dispatch</strong> to decrypt the
        recipient's postal address. This action is{' '}
        <strong>recorded in the audit log</strong> and requires a
        free-text reason that ties the decrypt to a postal batch.
      </p>
      <p className="mt-2 text-xs text-stone-500">
        Refreshing or navigating away returns to this gate; the
        plaintext address is held only in this browser session.
      </p>
      <button
        type="button"
        onClick={onClick}
        className="mt-4 rounded-md border border-accent bg-white px-3 py-1
                   text-sm font-medium text-accent shadow-sm hover:bg-accent/5"
      >
        Begin dispatch
      </button>
    </section>
  )
}

interface RevealedAddressProps {
  revealed: PhysicalMailDecryptResultData
  onPrint: () => void
  onMarkDispatched: () => void
  onMarkReceived: () => void
  onMarkFailed: () => void
}

function RevealedAddress({
  revealed, onPrint,
  onMarkDispatched, onMarkReceived, onMarkFailed,
}: RevealedAddressProps) {
  const address = revealed.address
  return (
    <section
      className="rounded-lg border border-amber-300 bg-amber-50 p-6 shadow-sm"
      data-testid="revealed-address"
    >
      <div className="flex items-baseline justify-between">
        <h2 className="text-base font-medium text-amber-900">
          Recipient address (audited disclosure)
        </h2>
        <button
          type="button"
          onClick={onPrint}
          className="rounded-md border border-amber-700 bg-white px-2 py-0.5
                     text-xs font-medium text-amber-900 shadow-sm
                     hover:bg-amber-100"
        >
          Print letter
        </button>
      </div>
      <pre
        className="mt-3 whitespace-pre-wrap rounded border border-amber-200
                   bg-white p-3 font-sans text-sm text-stone-900"
      >{formatAddress(address)}</pre>
      <p className="mt-2 text-xs text-amber-900">
        {revealed.dispatch_status_before ? (
          <>
            Disclosed via{' '}
            <CodeChip>{revealed.dispatch_status_before}</CodeChip>.{' '}
          </>
        ) : null}
        {revealed.cached ? (
          <>
            Served from the in-process decrypt cache (no new audit
            row). Mark the lifecycle action below once the postal
            hand-off is complete.
          </>
        ) : (
          <>
            Mark the lifecycle action below once the postal hand-off
            is complete.
          </>
        )}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onMarkDispatched}
          className="rounded-md border border-accent bg-white px-3 py-1
                     text-xs font-medium text-accent shadow-sm
                     hover:bg-accent/5"
        >
          Mark dispatched
        </button>
        <button
          type="button"
          onClick={onMarkReceived}
          className="rounded-md border border-emerald-600 bg-white px-3 py-1
                     text-xs font-medium text-emerald-700 shadow-sm
                     hover:bg-emerald-50"
        >
          Mark received (purges address)
        </button>
        <button
          type="button"
          onClick={onMarkFailed}
          className="rounded-md border border-red-300 bg-white px-3 py-1
                     text-xs font-medium text-red-700 shadow-sm
                     hover:bg-red-50"
        >
          Mark failed
        </button>
      </div>
    </section>
  )
}

function MutationSummary(
  { result }: { result: PhysicalMailMutationResultData },
) {
  return (
    <div
      className="rounded-md border border-stone-200 bg-stone-50 p-3 text-xs
                 text-stone-700"
      data-testid="mutation-summary"
    >
      Status changed{' '}
      <CodeChip>{result.dispatch_status_before}</CodeChip> →{' '}
      <CodeChip>{result.dispatch_status_after}</CodeChip>
      {result.tracking_number
        ? <> · tracking <CodeChip>{result.tracking_number}</CodeChip></>
        : null}
      {result.ciphertext_purged
        ? <> · ciphertext purged</>
        : null}
      {result.failure_reason
        ? <> · failure reason: {result.failure_reason}</>
        : null}
      .
    </div>
  )
}

function formatAddress(address: PhysicalAddressJson): string {
  const lines = [
    address.recipient_name,
    ...address.address_lines,
    [address.city, address.region, address.postal_code]
      .filter(Boolean).join(' '),
    address.country,
  ]
  return lines.filter(Boolean).join('\n')
}
